import React, { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";

const BREEZY_EMBED_SRC =
  "https://cornucopia-biosciences.breezy.hr/embed/js?inline=true&group_by=none";

const fade = (delay = 0, reduced = false) =>
  reduced
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-10% 0px" },
        transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay },
      };

const BreezyOpenings = () => {
  useEffect(() => {
    const scriptId = "breezy-openings-script";
    const timeoutId = window.setTimeout(() => {
      const container = document.getElementById("bzOpeningsContainer");
      const existingScript = document.getElementById(scriptId);

      if (container) {
        container.innerHTML = "";
      }

      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement("script");
      script.id = scriptId;
      script.src = BREEZY_EMBED_SRC;
      script.async = true;
      document.body.appendChild(script);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.getElementById(scriptId)?.remove();
      const container = document.getElementById("bzOpeningsContainer");
      if (container) {
        container.innerHTML = "";
      }
    };
  }, []);

  return (
    <div className="breezy-openings rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02]">
      <div id="bzOpeningsContainer" />
    </div>
  );
};

const Careers = () => {
  const reduced = useReducedMotion();

  return (
    <div className="bg-white dark:bg-[#050507] text-gray-900 dark:text-white">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <motion.p
          {...fade(0, reduced)}
          className="text-xs tracking-[0.2em] uppercase text-gray-400 dark:text-white/70 font-medium mb-5"
        >
          Careers
        </motion.p>
        <motion.h1
          {...fade(0.05, reduced)}
          className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif mb-5 leading-[1.1] tracking-tight"
        >
          Build the future of{" "}
          <span className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
            laboratory automation.
          </span>
        </motion.h1>
        <motion.p
          {...fade(0.1, reduced)}
          className="text-gray-500 dark:text-white/65 text-lg max-w-2xl leading-relaxed"
        >
          We're building practical automation infrastructure for life-science labs.
          Early stage, real responsibility, and a chance to shape how
          automation meets the lab.
        </motion.p>
      </div>

      <div className="min-[1920px]:mx-auto min-[1920px]:grid min-[1920px]:max-w-[80rem] min-[1920px]:grid-cols-[1.15fr_0.85fr] min-[1920px]:gap-8 min-[1920px]:px-6">
        {/* Job listings */}
        <div className="pb-20">
          <div className="max-w-3xl mx-auto px-6 space-y-5 min-[1920px]:max-w-none min-[1920px]:px-0">
            <motion.p
              {...fade(0, reduced)}
              className="text-xs tracking-[0.2em] uppercase text-gray-400 dark:text-white/70 font-medium mb-6"
            >
              Open roles
            </motion.p>

            <motion.div {...fade(0.06, reduced)}>
              <BreezyOpenings />
            </motion.div>
          </div>
        </div>

        {/* Why Cornucopia */}
        <div className="pb-20 min-[1920px]:pt-[2.9rem]">
          <motion.div
            {...fade(0, reduced)}
            className="max-w-3xl mx-auto px-6 min-[1920px]:max-w-none min-[1920px]:px-0"
          >
            <div className="rounded-xl p-10 border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02]">
            <p className="text-xs tracking-[0.2em] uppercase text-gray-400 dark:text-white/70 font-medium mb-4">
              Why Cornucopia
            </p>
            <h3 className="text-2xl md:text-3xl font-bold mb-4 tracking-tight">
              Your fingerprints on everything.
            </h3>
            <p className="text-gray-500 dark:text-white/65 text-sm mb-8 max-w-xl leading-relaxed">
              Work directly with founders who have deep experience in
              large-scale pharma and real-world scientific workflows. Be
              early enough that you shape GTM, product, customers, and
              culture.
            </p>
            <div className="flex flex-wrap gap-6 text-sm text-gray-500 dark:text-white/65 mb-8">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                Early-stage equity
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                Remote-friendly
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                Real ownership
              </span>
            </div>
              <a
                href="mailto:hello@cornucopiabio.com?subject=Career Interest at Cornucopia Bio"
                className="inline-flex min-h-11 items-center bg-white text-[#050507] font-medium px-6 py-3 rounded-xl hover:bg-white/90 transition-colors duration-200"
              >
                Get in touch
              </a>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 pb-20 min-[1920px]:max-w-[80rem]">
        <p className="text-sm italic leading-relaxed text-gray-500 dark:text-white/65">
          We are using an online service called{" "}
          <a
            href="http://Laborless.io"
            className="underline underline-offset-4 hover:text-gray-900 dark:hover:text-white"
          >
            Laborless.io
          </a>{" "}
          to post all electronic Labor Condition Applications (LCAs) and maintain
          electronic Public Access Files for our company. You can visit this link
          to review posted documents:{" "}
          <a
            href="https://laborless.io/dashboard/public/combined/petitioner/YOwoWxOw/"
            className="break-all underline underline-offset-4 hover:text-gray-900 dark:hover:text-white"
          >
            https://laborless.io/dashboard/public/combined/petitioner/YOwoWxOw/
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default Careers;
